import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  AlertCircle, BookOpen, Calendar, Clock, Flame, Heart,
  Moon, Music, Sparkles, Star, Sun, Sunrise, Sunset,
} from 'lucide-react';

/**
 * תבנית פסיפס — כל פריט כאריח ריבועי בפריסה אופקית.
 * קרם חם עם אריחים לבנים ופס צבעוני עליון לכל קטגוריה.
 */

const NAVY = '#1e3a5f';
const GOLD = '#9a7209';
const CREAM = '#f8f5ef';
const CARD_BG = '#ffffff';
const font = 'Frank Ruhl Libre, David, serif';

const CATEGORY_ICONS = {
  tachanun: AlertCircle, hallel: Music, prayerAdditions: BookOpen,
  seasonalPrayers: Sunrise, seasonalTransitions: Sun, selichot: Heart,
  weekdayPrayers: Clock, shabbatAdditions: Flame, motzaeiShabbat: Moon,
  seasonalCustoms: Star, liturgy: Sparkles, leyning: BookOpen,
  haftarah: BookOpen, mourningPeriods: Sunset, general: Calendar,
  limudim: BookOpen,
};

const CATEGORY_ACCENT = {
  tachanun: '#5a6d8a', hallel: '#c9a227', prayerAdditions: '#2d6a4f',
  seasonalPrayers: '#b45309', seasonalTransitions: '#b45309',
  selichot: '#8b5e6b', weekdayPrayers: '#5a6d8a',
  shabbatAdditions: '#8b6914', motzaeiShabbat: '#3d5a80',
  seasonalCustoms: '#6b7a3a', liturgy: '#6c5ce7',
  leyning: '#2d6a4f', haftarah: '#2d6a4f',
  mourningPeriods: '#8b5e5e', general: '#5a6d8a',
  limudim: '#1e3a5f',
};

/* ── אריח ריבועי בודד ── */
const MosaicTile = ({ item }) => {
  const isHigh = item.priority === 'high';
  const accent = CATEGORY_ACCENT[item.category] || '#5a6d8a';
  const IconComp = CATEGORY_ICONS[item.category] || Calendar;

  return (
    <Box sx={{
      width: 120, minHeight: 90,
      bgcolor: CARD_BG, borderRadius: '8px',
      border: `1px solid ${isHigh ? accent : '#e8e2d6'}`,
      borderTop: `3px solid ${accent}`,
      boxShadow: isHigh
        ? `0 2px 10px ${accent}22`
        : '0 1px 3px rgba(0,0,0,0.05)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      px: 1, py: 1, textAlign: 'center',
      flex: '0 0 auto',
    }}>
      <IconComp size={16} style={{ color: accent, marginBottom: 4 }} />

      <Typography sx={{
        fontFamily: font, fontSize: '0.62rem', fontWeight: 700,
        color: isHigh ? accent : NAVY, lineHeight: 1.3, mb: 0.25,
      }}>
        {item.label}
      </Typography>

      {item.value && (
        <Typography sx={{
          fontFamily: font, fontSize: '0.54rem', fontWeight: 500,
          color: '#6b7a8c', lineHeight: 1.25,
        }}>
          {item.value}
        </Typography>
      )}

      {item.detail && (
        <Typography sx={{
          fontFamily: font, fontSize: '0.48rem', fontWeight: 400,
          color: '#94a3b8', lineHeight: 1.2, mt: 0.15,
        }}>
          {item.detail}
        </Typography>
      )}
    </Box>
  );
};

/* ── Lookahead יום ── */
const LookaheadDay = ({ dayData }) => (
  <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #e0dbd0' }}>
    <Typography sx={{
      fontFamily: font, fontSize: '0.62rem', fontWeight: 700,
      color: NAVY, textAlign: 'center', mb: 0.75,
    }}>
      {dayData.hebrewDate?.hebrew || dayData.date}
    </Typography>
    {dayData.items.length === 0 ? (
      <Typography sx={{ fontFamily: font, fontSize: '0.55rem', color: '#94a3b8', textAlign: 'center' }}>
        אין ענייני יום מיוחדים
      </Typography>
    ) : (
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1,
      }}>
        {dayData.items.map((item, i) => (
          <MosaicTile key={item.id || `la-${i}`} item={item} />
        ))}
      </Box>
    )}
  </Box>
);

/* ══ Mosaic Template ══ */
const Mosaic = ({
  today, items, lookaheadItems, lookaheadDays,
  loading, error, title, showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const displayItems = items || [];

  return (
    <Box sx={{
      width: '100%', height: '100%',
      bgcolor: CREAM, direction: 'rtl', fontFamily: font,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Box sx={{
        flex: 1, overflow: 'auto', p: 1.5,
        '&::-webkit-scrollbar': { width: 2 },
        '&::-webkit-scrollbar-thumb': { background: '#d4c9b0', borderRadius: 1 },
      }}>
        {/* כותרת */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          {showTitle && (
            <Typography sx={{
              fontFamily: font, fontSize: '0.58rem', fontWeight: 500,
              color: '#8a8070', letterSpacing: '0.12em',
            }}>
              {title}
            </Typography>
          )}
          {hebrewDateStr && (
            <Typography sx={{
              fontFamily: font, fontSize: '0.85rem', fontWeight: 700,
              color: NAVY,
            }}>
              {hebrewDateStr}
            </Typography>
          )}
          <Box sx={{
            height: '1px', mt: 0.5, mx: 'auto', maxWidth: '50%',
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            opacity: 0.3,
          }} />
        </Box>

        {/* Loading / Error */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={18} sx={{ color: NAVY }} />
          </Box>
        )}
        {error && (
          <Typography sx={{ fontSize: '0.6rem', color: '#ef4444', textAlign: 'center' }}>
            שגיאה בטעינה
          </Typography>
        )}

        {/* ריק */}
        {displayItems.length === 0 && !loading && (
          <Typography sx={{ fontFamily: font, fontSize: '0.65rem', color: '#94a3b8', textAlign: 'center', mt: 2 }}>
            אין ענייני יום מיוחדים להיום
          </Typography>
        )}

        {/* פסיפס אריחים — פריסה אופקית */}
        {displayItems.length > 0 && (
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
            gap: 1, py: 0.5,
          }}>
            {displayItems.map((item, i) => (
              <MosaicTile key={item.id || `item-${i}`} item={item} />
            ))}
          </Box>
        )}

        {/* Lookahead */}
        {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
          lookaheadItems.map((dayData, i) => (
            <LookaheadDay key={dayData.date || i} dayData={dayData} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default Mosaic;
